/*
 * Pure helpers for the service form. Kept out of the component module so they
 * can be tested without pulling in the dashboard's provider tree.
 */

import { parseDollarsToCents } from "../../../util/formatCurrency";
import { defaultsFor, fieldsFor, nounFor } from "./serviceTypes";

const MAX_SPLIT_AMOUNT = 1000000;

// Sentinel for the "create a new group" option; can never collide with a real
// group name because the select only ever offers names already in use.
export const NEW_GROUP = "__new__";

export const centsToDollars = (cents) => (cents / 100).toFixed(2);

/*
 * resolveExclusionGroup — pure.
 *
 * The select yields either an existing group, "" for none, or the sentinel
 * meaning "use whatever was typed in the new-group field".
 */
export const resolveExclusionGroup = ({ selected, typed }) =>
  (selected === NEW_GROUP ? typed?.trim() : selected?.trim()) || null;

const toWholeNumber = (raw, fallback) => {
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

// The payload key each form field carries, and how its raw input becomes one.
const PAYLOAD_FIELDS = Object.freeze({
  price: {
    key: "splitAmount",
    parse: (raw) => parseDollarsToCents(`${raw ?? 0}`),
  },
  label: { key: "label", parse: (raw) => raw ?? "" },
  description: { key: "description", parse: (raw) => raw ?? "" },
  maxWordCount: {
    key: "maxWordCount",
    parse: (raw) => (raw ? toWholeNumber(raw, null) : null),
  },
  requiresTextInput: { key: "requiresTextInput", parse: Boolean },
  maxQuantity: { key: "maxQuantity", parse: (raw) => toWholeNumber(raw, 1) },
  exclusionGroup: { key: "exclusionGroup", parse: (raw) => raw ?? null },
});

/*
 * buildPayload — pure.
 *
 * A field the form showed comes from the form. A field it hid keeps whatever
 * the service already had, or takes its type's default when there is no
 * service yet — so simplifying the form can never silently rewrite a setting
 * an admin was never shown.
 */
export const buildPayload = ({
  category,
  values,
  existing = null,
  advancedRevealed = false,
}) => {
  const visible = new Set(fieldsFor(category, { advancedRevealed }));
  const hiddenValues = existing ?? defaultsFor(category);

  return Object.entries(PAYLOAD_FIELDS).reduce(
    (payload, [field, { key, parse }]) => ({
      ...payload,
      [key]: visible.has(field) ? parse(values[field]) : hiddenValues[key],
    }),
    { category },
  );
};

const asComparable = (value) => value ?? null;

/*
 * hiddenNonDefaults — pure.
 *
 * The settings a service holds that its own form would never show: what the
 * advanced disclosure exists to surface. Always empty for a type that shows
 * every field.
 */
export const hiddenNonDefaults = (service) =>
  Object.entries(defaultsFor(service.category))
    .filter(([field, fallback]) => asComparable(service[field]) !== fallback)
    .map(([field]) => ({ field, value: service[field] }));

const HIDDEN_FIELD_PHRASES = Object.freeze({
  maxWordCount: (value) => `a word limit of ${value}`,
  requiresTextInput: () => "no text asked of the shopper",
  maxQuantity: (value) => `a maximum of ${value} per product`,
  exclusionGroup: (value) => `the group “${value}”`,
});

const listPhrases = (phrases) =>
  new Intl.ListFormat("en", { style: "long", type: "conjunction" }).format(
    phrases,
  );

/*
 * describeAdvancedSettings — pure.
 *
 * One sentence naming what a service holds that its form cannot show, or null
 * when there is nothing to explain.
 */
export const describeAdvancedSettings = (service) => {
  const phrases = hiddenNonDefaults(service).map(({ field, value }) =>
    HIDDEN_FIELD_PHRASES[field](value),
  );

  return phrases.length === 0
    ? null
    : `This ${nounFor(
        service.category,
      ).toLowerCase()} uses advanced settings: ${listPhrases(phrases)}.`;
};

/*
 * validatePayload — pure.
 *
 * Returns `{ field, message }` for the first problem, or null. Naming the
 * field lets the caller mark that input invalid and move focus to it; a bare
 * string could only be printed somewhere and hoped for.
 */
export const validatePayload = (payload) => {
  const fail = (field, message) => ({ field, message });

  if (!payload.category) return fail("category", "Category is required");
  if (!payload.label.trim()) return fail("label", "Label is required");
  if (payload.label.length > 100) {
    return fail("label", "Label must be 100 characters or fewer");
  }
  if (payload.splitAmount <= 0) {
    return fail("price", "Price must be greater than $0");
  }
  if (payload.splitAmount > MAX_SPLIT_AMOUNT) {
    return fail("price", "Price must be $10,000 or less");
  }
  if (
    payload.maxWordCount !== null &&
    (payload.maxWordCount <= 0 || !Number.isInteger(payload.maxWordCount))
  ) {
    return fail("maxWordCount", "Word limit must be a positive whole number");
  }
  if (!Number.isInteger(payload.maxQuantity) || payload.maxQuantity < 1) {
    return fail("maxQuantity", "Max quantity must be a positive whole number");
  }
  if (
    payload.exclusionGroup !== null &&
    (typeof payload.exclusionGroup !== "string" ||
      payload.exclusionGroup.length === 0 ||
      payload.exclusionGroup.length > 50)
  ) {
    return fail(
      "exclusionGroup",
      "Exclusion group must be 50 characters or fewer",
    );
  }
  return null;
};
